import apiClient from './client';

// Type definitions
export interface DisconnectedAccount {
  account_id: string;
  account_name: string;
  spend: number;
  unlinked_at: string;
}

export interface TierStatus {
  tier_type: 'standard' | 'seasonal';
  status: 'pending' | 'grace' | 'active' | 'warning' | 'disconnected';
  total_spend_30_day: number;
  total_spend_365_day: number;
  spend_by_account: Record<string, number>;
  account_count: number;
  required_spend: number;
  threshold_per_account: number;
  spend_period: string; // "30 days" or "12 months"
  meets_threshold: boolean;
  grace_period_end: string | null;
  warning_sent_at: string | null;
  days_until_warning_expires: number | null;
  // Disconnected state fields
  disconnected_at: string | null;
  disconnected_accounts: DisconnectedAccount[];
  days_until_disconnected_ends: number | null;
  // Grace period tracking from signup
  is_within_grace_period: boolean;
  days_until_grace_ends: number | null;
}

export interface TierSwitchRequest {
  new_tier_type: 'standard' | 'seasonal';
}

export interface TierSwitchResponse {
  success: boolean;
  message: string;
  new_tier_type?: 'standard' | 'seasonal';
}

export interface EligibilityCheckResponse {
  eligible: boolean;
  reason: string;
  requires_spend_check: boolean;
  grant_grace_period: boolean;
  grace_period_days: number | null;
  spend_threshold: number | null;
}

export interface SpendSummary {
  total_spend: number;
  spend_by_account: Record<string, Record<string, number>>; // {channel: {account_id: spend}}
  spend_by_channel: Record<string, number>; // {channel: total_spend}
  account_count: number;
  start_date: string;
  end_date: string;
  meets_threshold: boolean;
  required_spend: number;
}

// Tier history types
export type TierEventType = 'signup' | 'tier_change' | 'status_change' | 'account_disconnect' | 'upgrade_to_paid';
export type PlanType = 'free_tier' | 'subscription';
export type ChannelType = 'google_ads' | 'meta_ads';

export interface TierHistoryItem {
  id: number;
  event_type: TierEventType;
  plan_type: PlanType;
  plan_name: string;
  status: string;
  previous_plan_type?: PlanType | null;
  previous_plan_name?: string | null;
  previous_status?: string | null;
  ad_account_id?: string | null;
  channel_type?: ChannelType | null;
  spend_at_disconnection?: number | null;
  metrics_snapshot?: {
    total_spend_30d?: number;
    total_spend_365d?: number;
    spend_by_channel?: Record<string, number>;
    linked_accounts_count?: number;
  } | null;
  transition_reason?: string | null;
  triggered_by?: string | null;
  notes?: string | null;
  effective_at: string;
  created_at: string;
}

export interface TierHistoryResponse {
  history: TierHistoryItem[];
  total_count: number;
}

export interface TierHistoryParams {
  limit?: number;
  offset?: number;
  event_type?: TierEventType;
}

export interface SetTierTypeRequest {
  tier_type: 'standard' | 'seasonal';
}

export interface SetTierTypeResponse {
  success: boolean;
  message: string;
  tier_type: 'standard' | 'seasonal';
  status: string;
}

export const tierApi = {
  /**
   * Get the current tier status for the authenticated user.
   */
  async getStatus(): Promise<TierStatus> {
    const response = await apiClient.get<TierStatus>('/tier/status');
    return response.data;
  },

  /**
   * Switch between tier types (standard <-> seasonal).
   * @param newTierType The tier type to switch to
   */
  async switchTier(newTierType: 'standard' | 'seasonal'): Promise<TierSwitchResponse> {
    const response = await apiClient.post<TierSwitchResponse>('/tier/switch', {
      new_tier_type: newTierType,
    });
    return response.data;
  },

  /**
   * Check if a Google Ads account can be connected.
   * @param googleAdsAccountId The Google Ads customer ID to check
   * @param tierType The tier type to check against
   */
  async checkEligibility(
    googleAdsAccountId: string,
    tierType: 'standard' | 'seasonal' = 'standard'
  ): Promise<EligibilityCheckResponse> {
    const response = await apiClient.get<EligibilityCheckResponse>(
      `/tier/eligibility/${googleAdsAccountId}`,
      { params: { tier_type: tierType } }
    );
    return response.data;
  },

  /**
   * Get spend summary for the authenticated user.
   * @param days Number of days to look back (default 30, max 365)
   */
  async getSpendSummary(days: number = 30): Promise<SpendSummary> {
    const response = await apiClient.get<SpendSummary>('/tier/spend', {
      params: { days },
    });
    return response.data;
  },

  /**
   * Set the tier type during onboarding.
   * @param tierType The tier type to set
   */
  async setTierType(tierType: 'standard' | 'seasonal'): Promise<SetTierTypeResponse> {
    const response = await apiClient.post<SetTierTypeResponse>('/tier/set-type', {
      tier_type: tierType,
    });
    return response.data;
  },

  /**
   * Force refresh the cached spend values.
   */
  async refreshSpend(): Promise<TierStatus> {
    const response = await apiClient.post<TierStatus>('/tier/refresh-spend');
    return response.data;
  },

  /**
   * Get tier history for the authenticated user.
   * @param params Optional parameters for pagination and filtering
   */
  async getHistory(params?: TierHistoryParams): Promise<TierHistoryResponse> {
    const response = await apiClient.get<TierHistoryResponse>('/tier/history', {
      params: {
        limit: params?.limit,
        offset: params?.offset,
        event_type: params?.event_type,
      },
    });
    return response.data;
  },
};

export default tierApi;
